import { useState, useEffect } from "react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

// ══════════════════════════════════════════════════════════════════════════════
// STEP 1 — CONFIGURATION
// In local dev: copy .env.example → .env.local and fill in the four values.
// In Azure SWA: set the same keys under Configuration → Application settings.
// When REACT_APP_CLIENT_ID is absent the app automatically runs in demo mode.
// ══════════════════════════════════════════════════════════════════════════════
const CFG = {
  DEMO:    !process.env.REACT_APP_CLIENT_ID,
  TENANT:  process.env.REACT_APP_TENANT_ID  || "d22bfcb0-6830-449d-81e7-1d7ab0fbb98e",
  CLIENT:  process.env.REACT_APP_CLIENT_ID  || "44bd7010-6309-441c-bbcd-73fe1248c953",
  SITE_ID: process.env.REACT_APP_SITE_ID    || "sigmacorp1.sharepoint.com,134c0afb-32be-4ea8-8c34-7538485039ca,e0e0b268-2787-446c-94c6-03f7b23cb932",
  LIST_ID: process.env.REACT_APP_LIST_ID    || "46e74e80-cc2c-417f-b4d2-fb8d06476eda",
  SCOPES:  ["Sites.ReadWrite.All", "Mail.Send", "User.Read"],
};

// ══════════════════════════════════════════════════════════════════════════════
// STEP 2 — MSAL AUTHENTICATION
// Run: npm install @azure/msal-browser
// After install, this block initialises automatically when CFG.DEMO is false.
// In the Claude artifact preview it runs in demo mode (msal-browser not loaded).
// ══════════════════════════════════════════════════════════════════════════════
let _msalApp = null;

async function getMsalApp() {
  if (_msalApp) return _msalApp;
  const { PublicClientApplication } = await import("@azure/msal-browser");
  _msalApp = new PublicClientApplication({
    auth: {
      clientId:    CFG.CLIENT,
      authority:   `https://login.microsoftonline.com/${CFG.TENANT}`,
      redirectUri: window.location.origin,
    },
    cache: { cacheLocation: "sessionStorage", storeAuthStateInCookie: false },
  });
  await _msalApp.initialize();
  return _msalApp;
}

async function getToken() {
  const app = await getMsalApp();
  await app.handleRedirectPromise();
  let accounts = app.getAllAccounts();
  if (!accounts.length) {
    await app.loginPopup({ scopes: CFG.SCOPES });
    accounts = app.getAllAccounts();
  }
  const account = accounts[0];
  try {
    const r = await app.acquireTokenSilent({ scopes: CFG.SCOPES, account });
    return r.accessToken;
  } catch {
    const r = await app.acquireTokenPopup({ scopes: CFG.SCOPES, account });
    return r.accessToken;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// STEP 3 — MICROSOFT GRAPH API DATA LAYER
// This replaces: Power Automate flows, 4 HTTP PATCH actions, hardcoded emails,
// and the 2,000-record Power Apps delegation limit — in one place.
// ══════════════════════════════════════════════════════════════════════════════
const G = "https://graph.microsoft.com/v1.0";
const H = (t) => ({ Authorization: `Bearer ${t}`, "Content-Type": "application/json" });

const Api = {
  // List all SDR submissions — paginated, no 2,000 cap
  async listSDRs(token) {
    const r = await fetch(`${G}/sites/${CFG.SITE_ID}/lists/${CFG.LIST_ID}/items?$expand=fields&$top=999`, { headers: H(token) });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error?.message || "Failed to load SDRs");
    return d.value.map(i => {
      const fields = { _itemId: i.id, ...i.fields };
      // SDRNumber internal name is SDRNumber0 — map it back for the UI
      if (fields.SDRNumber0 !== undefined) { fields.SDRNumber = fields.SDRNumber0; }
      return fields;
    });
  },

  // Create SDR — replaces Power Automate trigger + first status PATCH
  async createSDR(token, fields) {
    const r = await fetch(`${G}/sites/${CFG.SITE_ID}/lists/${CFG.LIST_ID}/items`, {
      method: "POST", headers: H(token),
      body: JSON.stringify({ fields: { ...fields, Status: "Pending Engineering", SubmittedDate: new Date().toISOString() } }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error?.message || "Create failed");
    return d;
  },

  // Patch status / comments — single fn replaces all 4 Power Automate HTTP PATCH actions
  async patchSDR(token, itemId, updates) {
    const r = await fetch(`${G}/sites/${CFG.SITE_ID}/lists/${CFG.LIST_ID}/items/${itemId}/fields`, {
      method: "PATCH", headers: H(token), body: JSON.stringify(updates),
    });
    if (!r.ok) { const d = await r.json(); throw new Error(d.error?.message || "PATCH failed"); }
  },

  // Send email via Graph — replaces Power Automate "Send an email" action entirely
  async sendMail(token, to, subject, html) {
    await fetch(`${G}/me/sendMail`, {
      method: "POST", headers: H(token),
      body: JSON.stringify({
        message: {
          subject,
          body: { contentType: "HTML", content: html },
          toRecipients: [{ emailAddress: { address: to } }],
        },
        saveToSentItems: true,
      }),
    });
  },

  async getMe(token) {
    const r = await fetch(`${G}/me`, { headers: H(token) });
    return r.json();
  },
};

// Email templates — matching the build spec
const Email = {
  engRequest: (s) => ({
    to:      s.EngineeringApproverEmail,
    subject: `Action Required: SDR Approval — ${s.SDRNumber} — ${s.PartName}`,
    html: `<p style="font-family:sans-serif;font-size:14px">A new Supplier Deviation Request requires your Engineering/Quality review.</p>
<table style="font-family:sans-serif;font-size:14px;border-collapse:collapse">
  <tr><td style="padding:4px 20px 4px 0;color:#666">SDR Number:</td><td><strong>${s.SDRNumber}</strong></td></tr>
  <tr><td style="padding:4px 20px 4px 0;color:#666">Supplier:</td><td>${s.SupplierName} — ${s.SupplierLocation}</td></tr>
  <tr><td style="padding:4px 20px 4px 0;color:#666">Part:</td><td>${s.PartName} (${s.PartNumber})</td></tr>
  <tr><td style="padding:4px 20px 4px 0;color:#666">Submitted by:</td><td>${s.SubmittedBy}</td></tr>
  <tr><td style="padding:4px 20px 4px 0;color:#666">Change type:</td><td>${s.ChangeType} · ${s.DeviationType}</td></tr>
</table>
<p style="font-family:sans-serif;font-size:14px"><strong>Deviation:</strong><br>${s.DeviationDescription}</p>
<p><a href="https://sigmacorp1.sharepoint.com/sites/Engineering" style="color:#2E86FF;font-family:sans-serif">Open Engineering QC Forms →</a></p>`,
  }),

  scRequest: (s) => ({
    to:      s.SupplyChainApproverEmail,
    subject: `Action Required: SDR Supply Chain Sign-off — ${s.SDRNumber}`,
    html: `<p style="font-family:sans-serif;font-size:14px">Engineering has approved the following SDR. Your sign-off is required to close it.</p>
<table style="font-family:sans-serif;font-size:14px;border-collapse:collapse">
  <tr><td style="padding:4px 20px 4px 0;color:#666">SDR:</td><td><strong>${s.SDRNumber}</strong></td></tr>
  <tr><td style="padding:4px 20px 4px 0;color:#666">Part:</td><td>${s.PartName} (${s.SupplierName})</td></tr>
  <tr><td style="padding:4px 20px 4px 0;color:#666">Engineering approved by:</td><td>${s.EngineeringApprover} on ${s.EngineeringApprovedDate?.split("T")[0]}</td></tr>
  <tr><td style="padding:4px 20px 4px 0;color:#666">Engineering comments:</td><td>${s.EngineeringComments || "—"}</td></tr>
</table>
<p><a href="https://sigmacorp1.sharepoint.com/sites/Engineering" style="color:#2E86FF;font-family:sans-serif">Open Engineering QC Forms →</a></p>`,
  }),

  closed: (s) => ({
    to:      s.SubmittedByEmail,
    subject: `SDR ${s.SDRNumber} — Approved & Closed`,
    html: `<p style="font-family:sans-serif;font-size:14px">Your Supplier Deviation Request has been fully approved by both Engineering and Supply Chain and is now <strong>Closed</strong>.</p>
<p style="font-family:sans-serif;font-size:14px"><strong>${s.SDRNumber}</strong> — ${s.PartName} (${s.SupplierName})</p>`,
  }),

  rejected: (s, byName, comments) => ({
    to:      s.SubmittedByEmail,
    subject: `SDR ${s.SDRNumber} — Rejected`,
    html: `<p style="font-family:sans-serif;font-size:14px">Your Supplier Deviation Request has been <strong>rejected</strong> by ${byName}.</p>
<p style="font-family:sans-serif;font-size:14px"><strong>${s.SDRNumber}</strong> — ${s.PartName} (${s.SupplierName})</p>
<p style="font-family:sans-serif;font-size:14px"><strong>Reason:</strong><br>${comments}</p>`,
  }),
};

// ─── Mock data (mirrors all SDR-Submissions SharePoint columns) ────────────────
const MOCK_SDRS = [
  {
    id:1, _itemId:"1", SDRNumber:"25-SDR091",
    SupplierName:"Precision Parts Co.", SupplierLocation:"Manchester, UK",
    SupplierContactName:"James Holloway", SupplierContactInfo:"j.holloway@precisionparts.co.uk",
    PartName:"Bearing Housing Assembly", PartNumber:"BHA-2241", PartDrawingRev:"DWG-2241 Rev D",
    ChangeType:"Design", DeviationType:"Temporary",
    DeviationDescription:"Material substitution from 316L to 304 stainless steel for bearing housing assembly due to global supply constraints on 316L stock.",
    ReasonForDeviation:"316L globally depleted; 304 meets tensile spec with 12% cost reduction and equivalent corrosion performance.",
    Consequences:"Marginally reduced corrosion resistance in salt-spray environments; acceptable for indoor installation.",
    RectificationPlan:"Supplier to revert to 316L on next production run Q3 2025. Incoming inspection to verify material cert.",
    AffectedProjects:"PRJ-2024-047", AffectedBatch:"BATCH-2025-BHA-04",
    CutOffSerialNumber:"SN-BHA-2241-0085", DeviationPermittedTime:"2025-09-30", DeviationPermittedQty:120,
    Status:"Pending Engineering", SubmittedDate:"2025-06-10",
    EngineeringApprover:"Sarah Chen", EngineeringApproverEmail:"sarah.chen@sigmaco.com",
    SupplyChainApprover:"Marcus Webb", SupplyChainApproverEmail:"marcus.webb@sigmaco.com",
    SubmittedBy:"Raju Kakani", SubmittedByEmail:"rk2@sigmaco.com",
  },
  {
    id:2, _itemId:"2", SDRNumber:"25-SDR087",
    SupplierName:"Axiom Castings Ltd.", SupplierLocation:"Birmingham, UK",
    SupplierContactName:"Priya Mehta", SupplierContactInfo:"+44 121 555 0182",
    PartName:"Gearbox Cover Plate", PartNumber:"GCP-0441", PartDrawingRev:"DWG-0441 Rev B",
    ChangeType:"Process", DeviationType:"Temporary",
    DeviationDescription:"Surface roughness Ra 3.2 μm delivered instead of specified Ra 1.6 μm on mating flange face.",
    ReasonForDeviation:"CNC calibration drift discovered post-production; re-calibration scheduled within 48 hours.",
    Consequences:"Potential micro-leakage at flange seal; function testing required before assembly.",
    RectificationPlan:"100% dimensional inspection on receipt. Function test on first-off assembly. Next batch to standard.",
    AffectedProjects:"PRJ-2025-003", AffectedBatch:"BATCH-2025-GCP-07",
    CutOffSerialNumber:"", DeviationPermittedTime:"2025-07-15", DeviationPermittedQty:50,
    Status:"Pending Supply Chain", SubmittedDate:"2025-06-08",
    EngineeringApprover:"Sarah Chen", EngineeringApproverEmail:"sarah.chen@sigmaco.com",
    EngineeringApprovedDate:"2025-06-09",
    EngineeringComments:"Acceptable for this batch only. Function test each unit. Tighten incoming inspection.",
    SupplyChainApprover:"Marcus Webb", SupplyChainApproverEmail:"marcus.webb@sigmaco.com",
    SubmittedBy:"Raju Kakani", SubmittedByEmail:"rk2@sigmaco.com",
  },
  {
    id:3, _itemId:"3", SDRNumber:"25-SDR083",
    SupplierName:"Euro Fasteners GmbH", SupplierLocation:"Stuttgart, DE",
    SupplierContactName:"Klaus Brenner", SupplierContactInfo:"k.brenner@eurofasteners.de",
    PartName:"M12 Hex Bolt Set", PartNumber:"HBS-M12-A4", PartDrawingRev:"DWG-HBS-M12 Rev A",
    ChangeType:"Process", DeviationType:"Permanent",
    DeviationDescription:"Zinc-nickel electroplating substituted for standard hot-dip galvanising on M12 fastener batch 2025-Q2.",
    ReasonForDeviation:"Galvanising facility offline for maintenance; zinc-nickel per ISO 4042 provides equivalent corrosion resistance.",
    Consequences:"No functional impact. Zinc-nickel offers superior adhesion and slightly thinner coating profile.",
    RectificationPlan:"Update drawing DWG-HBS-M12 to permit both finishes. Update approved supplier list.",
    AffectedProjects:"All active projects using HBS-M12-A4",
    AffectedBatch:"BATCH-2025-HBS-Q2", CutOffSerialNumber:"",
    DeviationPermittedTime:"", DeviationPermittedQty:0,
    Status:"Closed", SubmittedDate:"2025-05-28",
    EngineeringApprover:"Sarah Chen", EngineeringApproverEmail:"sarah.chen@sigmaco.com",
    EngineeringApprovedDate:"2025-05-29",
    EngineeringComments:"Salt spray test — 720-hour pass per ISO 9227. Approved permanent change.",
    SupplyChainApprover:"Marcus Webb", SupplyChainApproverEmail:"marcus.webb@sigmaco.com",
    SupplyChainApprovedDate:"2025-05-30", ClosedDate:"2025-05-30",
    SubmittedBy:"Raju Kakani", SubmittedByEmail:"rk2@sigmaco.com",
  },
  {
    id:4, _itemId:"4", SDRNumber:"25-SDR079",
    SupplierName:"Nordic Forgings AS", SupplierLocation:"Oslo, NO",
    SupplierContactName:"Erik Strand", SupplierContactInfo:"e.strand@nordicforgings.no",
    PartName:"Connecting Rod Blank", PartNumber:"CRB-7714", PartDrawingRev:"DWG-7714 Rev C",
    ChangeType:"Design", DeviationType:"Temporary",
    DeviationDescription:"Forging flash exceeds tolerance by 0.8 mm on parting line; affects non-critical face only.",
    ReasonForDeviation:"Die wear discovered beyond inspection interval; replacement die ordered.",
    Consequences:"", RectificationPlan:"",
    AffectedBatch:"BATCH-2025-CRB-02", AffectedProjects:"PRJ-2024-091",
    CutOffSerialNumber:"SN-CRB-7714-0042", DeviationPermittedTime:"", DeviationPermittedQty:0,
    Status:"Rejected", SubmittedDate:"2025-05-20",
    EngineeringApprover:"Sarah Chen", EngineeringApproverEmail:"sarah.chen@sigmaco.com",
    RejectionComments:"Parting line face is datum for sub-assembly. Cannot accept 0.8 mm overrun. Supplier to rework and resubmit.",
    SubmittedBy:"Raju Kakani", SubmittedByEmail:"rk2@sigmaco.com",
  },
  {
    id:5, _itemId:"5", SDRNumber:"25-SDR096",
    SupplierName:"Sigma Castings", SupplierLocation:"Leeds, UK",
    SupplierContactName:"", SupplierContactInfo:"",
    PartName:"Pump Body", PartNumber:"PBY-3320", PartDrawingRev:"DWG-3320 Rev A",
    ChangeType:"Design", DeviationType:"Temporary",
    DeviationDescription:"", ReasonForDeviation:"",
    Consequences:"", RectificationPlan:"",
    AffectedProjects:"", AffectedBatch:"", CutOffSerialNumber:"",
    DeviationPermittedTime:"", DeviationPermittedQty:0,
    Status:"Draft", SubmittedDate:"2025-06-12",
    EngineeringApprover:"Sarah Chen", EngineeringApproverEmail:"sarah.chen@sigmaco.com",
    SupplyChainApprover:"Marcus Webb", SupplyChainApproverEmail:"marcus.webb@sigmaco.com",
    SubmittedBy:"Raju Kakani", SubmittedByEmail:"rk2@sigmaco.com",
  },
];

const BAR_DATA = [
  { m:"Feb", n:3 },{ m:"Mar", n:5 },{ m:"Apr", n:4 },{ m:"May", n:7 },{ m:"Jun", n:5 },
];

// ─── Design tokens ─────────────────────────────────────────────────────────────
const C = { navy:"#134E4A", blue:"#0D9488", bg:"#F1F5F9", card:"#FFFFFF", text:"#1C1C1B", muted:"#64748B", border:"#E2E8F0", purple:"#7C3AED" };

const STATUS = {
  "Draft":                { bg:"#F1F5F9", color:"#475569", dot:"#94A3B8" },
  "Pending Engineering":  { bg:"#FFFBEB", color:"#92400E", dot:"#F59E0B" },
  "Pending Supply Chain": { bg:"#EFF6FF", color:"#1E40AF", dot:"#3B82F6" },
  "Closed":               { bg:"#F0FDF4", color:"#166534", dot:"#22C55E" },
  "Rejected":             { bg:"#FEF2F2", color:"#991B1B", dot:"#EF4444" },
};

// ─── Shared UI ─────────────────────────────────────────────────────────────────
const inp = { padding:"8px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, color:C.text, background:"#fff", boxSizing:"border-box", outline:"none", fontFamily:"inherit", width:"100%" };
const ta  = { ...inp, resize:"vertical", lineHeight:1.6 };
const lbl = { fontSize:12, fontWeight:500, color:C.muted, marginBottom:4, display:"block" };

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS["Draft"];
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, background:s.bg, color:s.color, fontSize:11, fontWeight:500, padding:"3px 10px", borderRadius:99, whiteSpace:"nowrap" }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:s.dot, flexShrink:0 }} />
      {status}
    </span>
  );
}

function StatusPipeline({ status }) {
  const steps  = ["Draft","Pending Engineering","Pending Supply Chain","Closed"];
  const labels = ["Draft","Eng. Review","SC Review","Closed"];
  const rejected = status === "Rejected";
  const idx = steps.indexOf(status);
  return (
    <div style={{ display:"flex", alignItems:"flex-start", width:"100%" }}>
      {steps.map((step, i) => {
        const done  = !rejected && idx > i;
        const active = !rejected && idx === i;
        return (
          <div key={step} style={{ display:"flex", alignItems:"center", flex:1 }}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
              <div style={{ width:28, height:28, borderRadius:"50%",
                background: done ? "#22C55E" : active ? C.blue : "#E2E8F0",
                border:`2px solid ${done ? "#22C55E" : active ? C.blue : "#CBD5E1"}`,
                display:"flex", alignItems:"center", justifyContent:"center",
                color:(done||active) ? "#fff" : "#94A3B8", fontSize:11, fontWeight:600 }}>
                {done ? "✓" : i + 1}
              </div>
              <span style={{ fontSize:10, textAlign:"center", lineHeight:1.2,
                color: active ? C.blue : done ? "#22C55E" : C.muted,
                fontWeight: active ? 600 : 400 }}>{labels[i]}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ height:2, flex:0.4, background: done ? "#22C55E" : "#E2E8F0", marginBottom:22 }} />
            )}
          </div>
        );
      })}
      {rejected && (
        <div style={{ alignSelf:"flex-start", marginTop:4, marginLeft:10, padding:"3px 10px", background:"#FEF2F2", color:"#991B1B", borderRadius:99, fontSize:11, fontWeight:500 }}>
          ✗ Rejected
        </div>
      )}
    </div>
  );
}

function KPICard({ label, value, sub, color }) {
  return (
    <div style={{ flex:1, background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"16px 20px" }}>
      <div style={{ fontSize:12, color:C.muted, marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:26, fontWeight:600, lineHeight:1, color: color || C.text }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>{sub}</div>}
    </div>
  );
}



// ─── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard({ sdrs, loading }) {
  const counts  = sdrs.reduce((a, s) => { a[s.Status]=(a[s.Status]||0)+1; return a; }, {});
  const pieData = Object.entries(counts).map(([name,value]) => ({name,value}));
  if (loading) return <div style={{ padding:64, textAlign:"center", color:C.muted, fontSize:13 }}>Loading from SharePoint…</div>;
  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:20, fontWeight:600, color:C.text, margin:0 }}>Dashboard</h1>
        <p style={{ fontSize:13, color:C.muted, margin:"3px 0 0" }}>SDR overview · Engineering QC · Sigma Engineering</p>
      </div>
      <div style={{ display:"flex", gap:12, marginBottom:20 }}>
        <KPICard label="Total SDRs" value={sdrs.length} sub="All time" />
        <KPICard label="Pending Engineering"  value={sdrs.filter(s=>s.Status==="Pending Engineering").length}  color="#F59E0B" />
        <KPICard label="Pending Supply Chain" value={sdrs.filter(s=>s.Status==="Pending Supply Chain").length} color={C.blue} />
        <KPICard label="Closed" value={sdrs.filter(s=>s.Status==="Closed").length} color="#22C55E" sub="Fully approved" />
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"250px 1fr", gap:16, marginBottom:20 }}>
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
          <div style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:14 }}>Status breakdown</div>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={38} outerRadius={62} paddingAngle={3} dataKey="value">
                {pieData.map((e,i) => <Cell key={i} fill={STATUS[e.name]?.dot||"#94A3B8"} />)}
              </Pie>
              <Tooltip formatter={(v,n)=>[v,n]} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display:"flex", flexDirection:"column", gap:5, marginTop:10 }}>
            {pieData.map(d => (
              <div key={d.name} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:11 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ width:8, height:8, borderRadius:2, background:STATUS[d.name]?.dot, flexShrink:0 }} />
                  <span style={{ color:C.muted }}>{d.name}</span>
                </div>
                <span style={{ fontWeight:500, color:C.text }}>{d.value}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
          <div style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:14 }}>Submissions per month</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={BAR_DATA} margin={{top:0,right:10,left:-20,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="m" tick={{fontSize:11,fill:C.muted}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontSize:11,fill:C.muted}} axisLine={false} tickLine={false} />
              <Tooltip />
              <Bar dataKey="n" fill={C.blue} radius={[4,4,0,0]} name="SDRs" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
        <div style={{ padding:"12px 18px", borderBottom:`1px solid ${C.border}`, fontSize:12, fontWeight:500, color:C.text }}>Recent submissions</div>
        {sdrs.slice(0,4).map(s => (
          <div key={s.id||s._itemId} style={{ display:"flex", alignItems:"center", padding:"11px 18px", borderBottom:`1px solid ${C.border}`, gap:12 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:500, color:C.text }}>{s.SDRNumber} · {s.PartName}</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{s.SupplierName} · {(s.SubmittedDate||"").split("T")[0]}</div>
            </div>
            <StatusBadge status={s.Status} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── New SDR form ──────────────────────────────────────────────────────────────
// Section header helper
function Sec({ title }) {
  return (
    <div style={{ fontSize:10, fontWeight:500, color:C.muted, textTransform:"uppercase",
      letterSpacing:".07em", padding:"14px 0 8px", borderBottom:`1px solid ${C.border}`,
      marginBottom:14, marginTop:4 }}>
      {title}
    </div>
  );
}

function NewSDR({ user, onSubmit }) {
  const blank = {
    // Identity
    SDRNumber:"25-SDR097", ChangeType:"Design", DeviationType:"Temporary",
    // Supplier
    SupplierName:"", SupplierLocation:"", SupplierContactName:"", SupplierContactInfo:"",
    // Part
    PartName:"", PartNumber:"", PartDrawingRev:"",
    // Deviation
    DeviationDescription:"", ReasonForDeviation:"", Consequences:"", RectificationPlan:"",
    // Scope
    AffectedProjects:"", AffectedBatch:"", CutOffSerialNumber:"",
    DeviationPermittedTime:"", DeviationPermittedQty:"",
    // Approval routing
    EngineeringApprover:"Sarah Chen", EngineeringApproverEmail:"sarah.chen@sigmaco.com",
    SupplyChainApprover:"Marcus Webb", SupplyChainApproverEmail:"marcus.webb@sigmaco.com",
    // Auto-set
    SubmittedBy: user?.name || "", SubmittedByEmail: user?.mail || "",
    CompletedForm:"",
  };
  const [form, setForm]     = useState(blank);
  const [saving, setSaving] = useState(false);
  const [done, setDone]     = useState(false);
  const [err, setErr]       = useState(null);
  const set = (k, v) => setForm(f => ({...f, [k]:v}));

  const handleSubmit = async () => {
    if (!form.SupplierName || !form.PartName || !form.DeviationDescription || !form.ReasonForDeviation) {
      setErr("Required fields missing: Supplier Name, Part Name, Deviation Description, and Reason for Deviation"); return;
    }
    setErr(null); setSaving(true);
    try { await onSubmit(form); setDone(true); }
    catch (e) { setErr(`Submit failed: ${e.message}`); }
    setSaving(false);
  };

  if (done) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:420, gap:16 }}>
      <div style={{ width:64, height:64, borderRadius:"50%", background:"#F0FDF4", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, color:"#22C55E" }}>✓</div>
      <div style={{ fontSize:18, fontWeight:500, color:C.text }}>SDR submitted</div>
      <div style={{ fontSize:13, color:C.muted, textAlign:"center", lineHeight:1.6 }}>
        <strong>{form.SDRNumber}</strong> is now <strong>Pending Engineering</strong><br />
        {form.EngineeringApprover} has been notified at {form.EngineeringApproverEmail}
      </div>
      <button onClick={() => { setDone(false); setForm({...blank, SDRNumber:"25-SDR098"}); }}
        style={{ padding:"9px 22px", background:C.blue, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:500 }}>
        Submit another SDR
      </button>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:20, fontWeight:600, color:C.text, margin:0 }}>New Supplier Deviation Request</h1>
        <p style={{ fontSize:13, color:C.muted, margin:"3px 0 0" }}>On submit: status → Pending Engineering · approver notified by email · all fields saved to SharePoint</p>
      </div>
      {err && <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, padding:"9px 14px", fontSize:13, color:"#991B1B", marginBottom:14 }}>✗ {err}</div>}

      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"20px 24px" }}>

        {/* ── SDR Identity ── */}
        <Sec title="SDR identity" />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:4 }}>
          <div><label style={lbl}>SDR number</label>
            <input style={inp} value={form.SDRNumber} onChange={e=>set("SDRNumber",e.target.value)} /></div>
          <div><label style={lbl}>Change type</label>
            <select style={inp} value={form.ChangeType} onChange={e=>set("ChangeType",e.target.value)}>
              <option>Design</option><option>Process</option>
            </select></div>
          <div><label style={lbl}>Deviation type</label>
            <select style={inp} value={form.DeviationType} onChange={e=>set("DeviationType",e.target.value)}>
              <option>Temporary</option><option>Permanent</option>
            </select></div>
        </div>

        {/* ── Supplier ── */}
        <Sec title="Supplier" />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:10 }}>
          <div><label style={lbl}>Supplier name *</label>
            <input style={inp} value={form.SupplierName} onChange={e=>set("SupplierName",e.target.value)} placeholder="e.g. Precision Parts Co." /></div>
          <div><label style={lbl}>Supplier location</label>
            <input style={inp} value={form.SupplierLocation} onChange={e=>set("SupplierLocation",e.target.value)} placeholder="City, Country" /></div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:4 }}>
          <div><label style={lbl}>Contact name</label>
            <input style={inp} value={form.SupplierContactName} onChange={e=>set("SupplierContactName",e.target.value)} placeholder="Contact person at supplier" /></div>
          <div><label style={lbl}>Contact info</label>
            <input style={inp} value={form.SupplierContactInfo} onChange={e=>set("SupplierContactInfo",e.target.value)} placeholder="Phone or email" /></div>
        </div>

        {/* ── Part ── */}
        <Sec title="Part" />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:4 }}>
          <div><label style={lbl}>Part name *</label>
            <input style={inp} value={form.PartName} onChange={e=>set("PartName",e.target.value)} placeholder="e.g. Bearing Housing Assembly" /></div>
          <div><label style={lbl}>Part number *</label>
            <input style={inp} value={form.PartNumber} onChange={e=>set("PartNumber",e.target.value)} placeholder="e.g. BHA-2241" /></div>
          <div><label style={lbl}>Drawing / revision</label>
            <input style={inp} value={form.PartDrawingRev} onChange={e=>set("PartDrawingRev",e.target.value)} placeholder="e.g. DWG-2241 Rev D" /></div>
        </div>

        {/* ── Deviation ── */}
        <Sec title="Deviation" />
        <div style={{ marginBottom:12 }}>
          <label style={{...lbl,margin:0}}>Deviation description *</label>
          <textarea style={{...ta,minHeight:80}} value={form.DeviationDescription}
            onChange={e=>set("DeviationDescription",e.target.value)}
            placeholder="Describe the deviation from the approved specification…" />
        </div>
        <div style={{ marginBottom:12 }}>
          <label style={lbl}>Reason for deviation *</label>
          <textarea style={{...ta,minHeight:68}} value={form.ReasonForDeviation}
            onChange={e=>set("ReasonForDeviation",e.target.value)}
            placeholder="Root cause and justification…" />
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:4 }}>
          <div><label style={lbl}>Consequences</label>
            <textarea style={{...ta,minHeight:64}} value={form.Consequences}
              onChange={e=>set("Consequences",e.target.value)}
              placeholder="Risk or impact on quality, cost, or delivery…" /></div>
          <div><label style={lbl}>Rectification plan</label>
            <textarea style={{...ta,minHeight:64}} value={form.RectificationPlan}
              onChange={e=>set("RectificationPlan",e.target.value)}
              placeholder="Actions, deadlines, and references to PFD / FMEA…" /></div>
        </div>

        {/* ── Scope ── */}
        <Sec title="Scope" />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:10 }}>
          <div><label style={lbl}>Affected projects</label>
            <input style={inp} value={form.AffectedProjects} onChange={e=>set("AffectedProjects",e.target.value)} placeholder="Project / WO references" /></div>
          <div><label style={lbl}>Affected batch</label>
            <input style={inp} value={form.AffectedBatch} onChange={e=>set("AffectedBatch",e.target.value)} placeholder="Batch number(s)" /></div>
          <div><label style={lbl}>Cut-off serial number</label>
            <input style={inp} value={form.CutOffSerialNumber} onChange={e=>set("CutOffSerialNumber",e.target.value)} placeholder="Last serial affected" /></div>
        </div>
        {form.DeviationType === "Temporary" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:4 }}>
            <div><label style={lbl}>Permitted until (date)</label>
              <input style={inp} type="date" value={form.DeviationPermittedTime}
                onChange={e=>set("DeviationPermittedTime",e.target.value)} /></div>
            <div><label style={lbl}>Max permitted quantity</label>
              <input style={inp} type="number" min="0" value={form.DeviationPermittedQty}
                onChange={e=>set("DeviationPermittedQty",e.target.value)} placeholder="0" /></div>
          </div>
        )}

        {/* ── Approval routing ── */}
        <Sec title="Approval routing" />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:4 }}>
          <div>
            <label style={lbl}>Engineering approver *</label>
            <input style={inp} value={form.EngineeringApprover} onChange={e=>set("EngineeringApprover",e.target.value)} />
            <input style={{...inp,marginTop:6,fontSize:11,color:C.muted}} value={form.EngineeringApproverEmail}
              onChange={e=>set("EngineeringApproverEmail",e.target.value)} placeholder="approver@sigmaco.com" />
          </div>
          <div>
            <label style={lbl}>Supply chain approver *</label>
            <input style={inp} value={form.SupplyChainApprover} onChange={e=>set("SupplyChainApprover",e.target.value)} />
            <input style={{...inp,marginTop:6,fontSize:11,color:C.muted}} value={form.SupplyChainApproverEmail}
              onChange={e=>set("SupplyChainApproverEmail",e.target.value)} placeholder="approver@sigmaco.com" />
          </div>
        </div>

        {/* ── Completed form PDF ── */}
        <div style={{ marginTop:14 }}>
          <label style={lbl}>Completed form PDF (optional — paste SharePoint link)</label>
          <input style={{...inp,fontSize:12,color:C.muted}} value={form.CompletedForm}
            onChange={e=>set("CompletedForm",e.target.value)}
            placeholder="https://sigmacorp1.sharepoint.com/sites/Engineering/SDR-Forms/…" />
        </div>

        {/* ── Actions ── */}
        <div style={{ display:"flex", gap:10, justifyContent:"flex-end", borderTop:`1px solid ${C.border}`, paddingTop:18, marginTop:20 }}>
          <button style={{ padding:"9px 20px", background:"#fff", color:C.muted, border:`1px solid ${C.border}`, borderRadius:8, cursor:"pointer", fontSize:13 }}>
            Save as draft
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding:"9px 24px", background:saving?"#93C5FD":C.blue, color:"#fff", border:"none", borderRadius:8, cursor:saving?"not-allowed":"pointer", fontSize:13, fontWeight:500 }}>
            {saving ? "Submitting…" : "Submit for Engineering review →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── My Submissions ────────────────────────────────────────────────────────────
function MySubmissions({ sdrs }) {
  const [sel, setSel] = useState(null);
  if (sel) return (
    <div>
      <button onClick={()=>setSel(null)} style={{ display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:C.blue,cursor:"pointer",fontSize:13,padding:0,marginBottom:18 }}>← Back</button>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:24 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
          <div>
            <h2 style={{ margin:0, fontSize:18, fontWeight:600, color:C.text }}>{sel.SDRNumber}</h2>
            <p style={{ margin:"4px 0 0", fontSize:13, color:C.muted }}>{sel.PartName} · {sel.SupplierName}</p>
          </div>
          <StatusBadge status={sel.Status} />
        </div>
        <div style={{ marginBottom:20 }}><StatusPipeline status={sel.Status} /></div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
          {[["Part number",sel.PartNumber],["Supplier location",sel.SupplierLocation],
            ["Change type",sel.ChangeType],["Deviation type",sel.DeviationType],
            ["Submitted",(sel.SubmittedDate||"").split("T")[0]],["Engineering approver",sel.EngineeringApprover],
            ["Supply chain approver",sel.SupplyChainApprover||"—"],["Submitted by",sel.SubmittedBy],
          ].map(([k,v]) => (
            <div key={k} style={{ padding:"10px 14px", background:"#F8FAFC", borderRadius:8 }}>
              <div style={{ fontSize:11, color:C.muted }}>{k}</div>
              <div style={{ fontSize:13, color:C.text, fontWeight:500, marginTop:2 }}>{v||"—"}</div>
            </div>
          ))}
        </div>
        {sel.DeviationDescription && <div style={{ padding:"12px 14px", background:"#F8FAFC", borderRadius:8, marginBottom:10 }}>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Deviation description</div>
          <div style={{ fontSize:13, color:C.text, lineHeight:1.6 }}>{sel.DeviationDescription}</div>
        </div>}
        {sel.EngineeringComments && <div style={{ padding:"12px 14px", background:"#FFFBEB", borderRadius:8, marginBottom:10, borderLeft:"3px solid #F59E0B" }}>
          <div style={{ fontSize:11, color:"#92400E", marginBottom:4 }}>Engineering comments</div>
          <div style={{ fontSize:13, color:C.text, lineHeight:1.6 }}>{sel.EngineeringComments}</div>
        </div>}
        {sel.RejectionComments && <div style={{ padding:"12px 14px", background:"#FEF2F2", borderRadius:8, borderLeft:"3px solid #EF4444" }}>
          <div style={{ fontSize:11, color:"#991B1B", marginBottom:4 }}>Rejection comments</div>
          <div style={{ fontSize:13, color:C.text, lineHeight:1.6 }}>{sel.RejectionComments}</div>
        </div>}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:20, fontWeight:600, color:C.text, margin:0 }}>My Submissions</h1>
        <p style={{ fontSize:13, color:C.muted, margin:"3px 0 0" }}>Filtered to your submissions</p>
      </div>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead><tr style={{ background:"#F8FAFC" }}>
            {["SDR #","Part","Supplier","Change","Submitted","Status",""].map(h=>(
              <th key={h} style={{ padding:"10px 16px", textAlign:"left", fontSize:11, fontWeight:500, color:C.muted, borderBottom:`1px solid ${C.border}` }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {sdrs.map(s => (
              <tr key={s.id||s._itemId} style={{ borderBottom:`1px solid ${C.border}` }}>
                <td style={{ padding:"12px 16px", fontWeight:500, color:C.blue }}>{s.SDRNumber}</td>
                <td style={{ padding:"12px 16px", color:C.text }}>{s.PartName}</td>
                <td style={{ padding:"12px 16px", color:C.muted }}>{s.SupplierName}</td>
                <td style={{ padding:"12px 16px", color:C.muted }}>{s.ChangeType}</td>
                <td style={{ padding:"12px 16px", color:C.muted }}>{(s.SubmittedDate||"").split("T")[0]}</td>
                <td style={{ padding:"12px 16px" }}><StatusBadge status={s.Status} /></td>
                <td style={{ padding:"12px 16px" }}>
                  <button onClick={()=>setSel(s)} style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:11, color:C.muted }}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Approval Queue ────────────────────────────────────────────────────────────
function ApprovalQueue({ sdrs, user, onApprove, onReject }) {
  const pending = sdrs.filter(s => s.Status==="Pending Engineering"||s.Status==="Pending Supply Chain");
  const [decisions,setDecisions]= useState({});
  const [acting,   setActing]   = useState({});
  const [comments, setComments] = useState({});
  const [showCmt,  setShowCmt]  = useState({});

  const key = (s) => s.id||s._itemId;

  const doApprove = async (sdr) => {
    const k = key(sdr); setActing(a=>({...a,[k]:true}));
    await onApprove(sdr, comments[k]||"");
    setDecisions(d=>({...d,[k]:"Approve"})); setActing(a=>({...a,[k]:false}));
  };

  const doReject = async (sdr) => {
    const k = key(sdr);
    if (!comments[k]) { setShowCmt(s=>({...s,[k]:true})); return; }
    setActing(a=>({...a,[k]:true}));
    await onReject(sdr, comments[k]);
    setDecisions(d=>({...d,[k]:"Reject"})); setActing(a=>({...a,[k]:false}));
  };

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:20, fontWeight:600, color:C.text, margin:0 }}>Approval Queue</h1>
        <p style={{ fontSize:13, color:C.muted, margin:"3px 0 0" }}>{pending.length} item{pending.length!==1?"s":""} awaiting your decision</p>
      </div>
      {pending.length===0 && (
        <div style={{ textAlign:"center", padding:56, color:C.muted, fontSize:13, background:C.card, border:`1px solid ${C.border}`, borderRadius:12 }}>
          Queue is clear — no pending items
        </div>
      )}
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        {pending.map(sdr => {
          const k = key(sdr); const dec = decisions[k];
          return (
            <div key={k} style={{ background:C.card, border:`1px solid ${dec?(dec==="Approve"?"#BBF7D0":"#FECACA"):C.border}`, borderRadius:12, padding:20 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                    <span style={{ fontWeight:600, color:C.text, fontSize:14 }}>{sdr.SDRNumber}</span>
                    <StatusBadge status={sdr.Status} />
                    {dec && <span style={{ fontSize:11,fontWeight:500,padding:"2px 9px",borderRadius:99,
                      background:dec==="Approve"?"#F0FDF4":"#FEF2F2",color:dec==="Approve"?"#166534":"#991B1B" }}>
                      {dec==="Approve"?"✓ Approved":"✗ Rejected"}</span>}
                  </div>
                  <div style={{ fontSize:12, color:C.muted, marginTop:4 }}>
                    {sdr.PartName} · {sdr.PartNumber} · {sdr.SupplierName} · submitted {(sdr.SubmittedDate||"").split("T")[0]||sdr.SubmittedDate}
                  </div>
                </div>
              </div>
              <div style={{ fontSize:13, color:C.text, lineHeight:1.65, padding:"10px 12px", background:"#F8FAFC", borderRadius:8, marginBottom:12 }}>
                {sdr.DeviationDescription}
              </div>
              {(showCmt[k]||dec==="Reject") && !dec && <textarea
                value={comments[k]||""} onChange={e=>setComments(c=>({...c,[k]:e.target.value}))}
                placeholder="Enter rejection reason (required)…"
                style={{...ta,minHeight:60,marginBottom:12}} />}
              {!dec && <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                <button onClick={()=>doReject(sdr)} disabled={acting[k]}
                  style={{ padding:"7px 18px",background:"#fff",color:"#991B1B",border:"1px solid #FECACA",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:500 }}>
                  {showCmt[k]?(comments[k]?"Confirm reject":"Add reason above"):"Reject"}
                </button>
                <button onClick={()=>doApprove(sdr)} disabled={acting[k]}
                  style={{ padding:"7px 20px",background:acting[k]?"#86EFAC":"#22C55E",color:"#fff",border:"none",borderRadius:8,cursor:acting[k]?"not-allowed":"pointer",fontSize:12,fontWeight:500 }}>
                  {acting[k]?"Processing…":"Approve →"}
                </button>
              </div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Search & Archive ──────────────────────────────────────────────────────────
function SearchArchive({ sdrs }) {
  const [q, setQ] = useState("");
  const [sf, setSF] = useState("All");
  const results = sdrs.filter(s => {
    const matchQ = !q || [s.SDRNumber,s.PartName,s.SupplierName,s.PartNumber].some(v=>v?.toLowerCase().includes(q.toLowerCase()));
    return matchQ && (sf==="All"||s.Status===sf);
  });
  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:20, fontWeight:600, color:C.text, margin:0 }}>Search & Archive</h1>
        <p style={{ fontSize:13, color:C.muted, margin:"3px 0 0" }}>All SDR records · {sdrs.length} total</p>
      </div>
      <div style={{ display:"flex", gap:10, marginBottom:16 }}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search by SDR number, part, supplier, or part number…" style={{...inp,flex:1}} />
        <select value={sf} onChange={e=>setSF(e.target.value)} style={{...inp,width:"auto",cursor:"pointer"}}>
          {["All","Draft","Pending Engineering","Pending Supply Chain","Closed","Rejected"].map(s=><option key={s}>{s}</option>)}
        </select>
      </div>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
        <div style={{ padding:"10px 16px", borderBottom:`1px solid ${C.border}`, fontSize:11, color:C.muted }}>{results.length} result{results.length!==1?"s":""}</div>
        {results.length===0
          ? <div style={{ padding:36, textAlign:"center", color:C.muted, fontSize:13 }}>No matching records</div>
          : <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead><tr style={{ background:"#F8FAFC" }}>
                {["SDR #","Part","Supplier","Change","Type","Submitted","Status"].map(h=>(
                  <th key={h} style={{ padding:"10px 16px",textAlign:"left",fontSize:11,fontWeight:500,color:C.muted,borderBottom:`1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {results.map(s=>(
                  <tr key={s.id||s._itemId} style={{ borderBottom:`1px solid ${C.border}` }}>
                    <td style={{ padding:"11px 16px",fontWeight:500,color:C.blue }}>{s.SDRNumber}</td>
                    <td style={{ padding:"11px 16px",color:C.text }}>{s.PartName}</td>
                    <td style={{ padding:"11px 16px",color:C.muted }}>{s.SupplierName}</td>
                    <td style={{ padding:"11px 16px",color:C.muted }}>{s.ChangeType}</td>
                    <td style={{ padding:"11px 16px",color:C.muted }}>{s.DeviationType}</td>
                    <td style={{ padding:"11px 16px",color:C.muted }}>{(s.SubmittedDate||"").split("T")[0]||s.SubmittedDate}</td>
                    <td style={{ padding:"11px 16px" }}><StatusBadge status={s.Status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
        }
      </div>
    </div>
  );
}

// ─── Form Library ──────────────────────────────────────────────────────────────
function FormLibrary() {
  const FORMS = [
    { code:"SDR",    color:C.blue,   name:"Supplier Deviation Request",  desc:"Use when a supplier proposes or delivers a part or process that deviates from the approved specification.", version:"Rev 3.1", updated:"2025-04-12" },
    { code:"QCF-02", color:C.purple, name:"Quality Concern Form",        desc:"Internal quality concern reporting for production or inspection non-conformances.", version:"Rev 1.4", updated:"2025-03-05" },
    { code:"QCF-03", color:"#F59E0B",name:"Engineering Change Request",  desc:"Formal request to change an engineering drawing, specification, or process document.", version:"Rev 2.0", updated:"2025-05-01" },
  ];
  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:20, fontWeight:600, color:C.text, margin:0 }}>Form Library</h1>
        <p style={{ fontSize:13, color:C.muted, margin:"3px 0 0" }}>Download blank templates from SharePoint · Form-Templates library</p>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {FORMS.map(f => (
          <div key={f.code} style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"18px 20px",display:"flex",alignItems:"center",gap:16 }}>
            <div style={{ width:46,height:46,borderRadius:10,background:f.color+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:f.color,flexShrink:0 }}>{f.code}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:14, fontWeight:500, color:C.text }}>{f.name}</div>
              <div style={{ fontSize:12, color:C.muted, margin:"3px 0" }}>{f.desc}</div>
              <div style={{ fontSize:11, color:C.muted }}>{f.version} · Updated {f.updated}</div>
            </div>
            <button style={{ padding:"8px 16px",background:"#fff",color:C.blue,border:`1px solid ${C.border}`,borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:500,whiteSpace:"nowrap" }}>↓ Download PDF</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Setup: SharePoint Column Manager ─────────────────────────────────────────

// Type badge style map
const TYPE_TAG = {
  "Text":       { bg:"#EFF6FF", color:"#1E40AF" },
  "Multi-line": { bg:"#F5F3FF", color:"#5B21B6" },
  "Date":       { bg:"#F0FDF4", color:"#166534" },
  "Date only":  { bg:"#ECFDF5", color:"#065F46" },
  "Person":     { bg:"#FFF7ED", color:"#9A3412" },
  "Choice":     { bg:"#FFFBEB", color:"#92400E" },
  "Number":     { bg:"#FEF2F2", color:"#991B1B" },
  "Hyperlink":  { bg:"#F5F3FF", color:"#7C3AED" },
  "Built-in":   { bg:"#F1F5F9", color:"#475569" },
};

function TypeTag({ type }) {
  const s = TYPE_TAG[type] || TYPE_TAG["Text"];
  return (
    <span style={{ fontSize:10, fontWeight:500, padding:"2px 7px", borderRadius:99, background:s.bg, color:s.color, whiteSpace:"nowrap" }}>
      {type}
    </span>
  );
}

// All 32 required columns (Title built-in + 31 custom)
const ALL_COLS = [
  // group: "built-in" | "existing" | "add"
  // existing = was added manually before this tool existed
  // add      = the 12 new columns added by the setup script
  { name:"Title",                  type:"Built-in",  group:"built-in", desc:"Always present — auto-set to SDR number",                      payload:null },
  { name:"SDRNumber",              type:"Text",       group:"existing", desc:"SDR identifier, e.g. 25-SDR097",                               payload:{ name:"SDRNumber",               text:{} }},
  { name:"SupplierName",           type:"Text",       group:"existing", desc:"Supplier company name",                                        payload:{ name:"SupplierName",            text:{} }},
  { name:"SupplierLocation",       type:"Text",       group:"existing", desc:"City and country",                                             payload:{ name:"SupplierLocation",        text:{} }},
  { name:"PartName",               type:"Text",       group:"existing", desc:"Part or assembly name",                                        payload:{ name:"PartName",                text:{} }},
  { name:"PartNumber",             type:"Text",       group:"existing", desc:"Engineering part number",                                      payload:{ name:"PartNumber",              text:{} }},
  { name:"DeviationDescription",   type:"Multi-line", group:"existing", desc:"Full technical description of the deviation",                  payload:{ name:"DeviationDescription",    text:{ allowMultipleLines:true } }},
  { name:"ReasonForDeviation",     type:"Multi-line", group:"existing", desc:"Root cause and justification",                                 payload:{ name:"ReasonForDeviation",      text:{ allowMultipleLines:true } }},
  { name:"EngineeringComments",    type:"Multi-line", group:"existing", desc:"Engineering reviewer comments",                                payload:{ name:"EngineeringComments",     text:{ allowMultipleLines:true } }},
  { name:"SupplyChainComments",    type:"Multi-line", group:"existing", desc:"Supply Chain reviewer comments",                               payload:{ name:"SupplyChainComments",     text:{ allowMultipleLines:true } }},
  { name:"RejectionComments",      type:"Multi-line", group:"existing", desc:"Reason provided on rejection",                                 payload:{ name:"RejectionComments",       text:{ allowMultipleLines:true } }},
  { name:"SubmittedDate",          type:"Date",       group:"existing", desc:"Date and time the SDR was submitted",                          payload:{ name:"SubmittedDate",           dateTime:{ format:"dateTime" } }},
  { name:"EngineeringApprovedDate",type:"Date",       group:"existing", desc:"Date Engineering approved",                                    payload:{ name:"EngineeringApprovedDate", dateTime:{ format:"dateTime" } }},
  { name:"SupplyChainApprovedDate",type:"Date",       group:"existing", desc:"Date Supply Chain approved",                                   payload:{ name:"SupplyChainApprovedDate", dateTime:{ format:"dateTime" } }},
  { name:"ClosedDate",             type:"Date",       group:"existing", desc:"Date the SDR was fully closed",                                payload:{ name:"ClosedDate",              dateTime:{ format:"dateTime" } }},
  { name:"EngineeringApprover",    type:"Person",     group:"existing", desc:"Engineering/Quality approver — stage 1",                       payload:{ name:"EngineeringApprover",     personOrGroup:{ allowMultipleSelection:false, chooseFromType:"peopleOnly" } }},
  { name:"SupplyChainApprover",    type:"Person",     group:"existing", desc:"Supply Chain approver — stage 2",                              payload:{ name:"SupplyChainApprover",     personOrGroup:{ allowMultipleSelection:false, chooseFromType:"peopleOnly" } }},
  { name:"Status",                 type:"Choice",     group:"existing", desc:"Draft / Pending Engineering / Pending Supply Chain / Closed / Rejected", payload:{ name:"Status", choice:{ choices:["Draft","Pending Engineering","Pending Supply Chain","Closed","Rejected"], displayAs:"dropDownMenu" } }},
  { name:"ChangeType",             type:"Choice",     group:"existing", desc:"Design or Process",                                            payload:{ name:"ChangeType",  choice:{ choices:["Design","Process"],       displayAs:"dropDownMenu" } }},
  { name:"DeviationType",          type:"Choice",     group:"existing", desc:"Temporary or Permanent",                                       payload:{ name:"DeviationType",choice:{ choices:["Temporary","Permanent"], displayAs:"dropDownMenu" } }},
  // ── 12 new columns ────────────────────────────────────────────────────────
  { name:"SupplierContactName",    type:"Text",       group:"add",      desc:"Contact person at the supplier",                               payload:{ name:"SupplierContactName",     text:{} }},
  { name:"SupplierContactInfo",    type:"Text",       group:"add",      desc:"Phone or email of supplier contact",                           payload:{ name:"SupplierContactInfo",     text:{} }},
  { name:"PartDrawingRev",         type:"Text",       group:"add",      desc:"Drawing number and revision, e.g. DWG-2241 Rev D",             payload:{ name:"PartDrawingRev",          text:{} }},
  { name:"Consequences",           type:"Multi-line", group:"add",      desc:"Risk or impact on quality, cost, or delivery",                  payload:{ name:"Consequences",            text:{ allowMultipleLines:true } }},
  { name:"RectificationPlan",      type:"Multi-line", group:"add",      desc:"Corrective actions, deadlines, PFD/FMEA references",           payload:{ name:"RectificationPlan",       text:{ allowMultipleLines:true } }},
  { name:"AffectedProjects",       type:"Text",       group:"add",      desc:"Project or work order references",                             payload:{ name:"AffectedProjects",        text:{} }},
  { name:"AffectedBatch",          type:"Text",       group:"add",      desc:"Production batch numbers affected",                            payload:{ name:"AffectedBatch",           text:{} }},
  { name:"CutOffSerialNumber",     type:"Text",       group:"add",      desc:"Last serial number affected by this deviation",                payload:{ name:"CutOffSerialNumber",      text:{} }},
  { name:"DeviationPermittedTime", type:"Date only",  group:"add",      desc:"Expiry date for temporary deviations",                         payload:{ name:"DeviationPermittedTime",  dateTime:{ format:"dateOnly" } }},
  { name:"DeviationPermittedQty",  type:"Number",     group:"add",      desc:"Maximum quantity permitted under this deviation",              payload:{ name:"DeviationPermittedQty",   number:{ decimalPlaces:"none", minimum:0 } }},
  { name:"SubmittedBy",            type:"Person",     group:"add",      desc:"Initiator — auto-populated by the app",                        payload:{ name:"SubmittedBy",             personOrGroup:{ allowMultipleSelection:false, chooseFromType:"peopleOnly" } }},
  { name:"CompletedForm",          type:"Hyperlink",  group:"add",      desc:"Link to the completed PDF in SDR-Forms library",               payload:{ name:"CompletedForm",           hyperlinkOrPicture:{ isPicture:false } }},
];

function Setup({ token }) {
  const [existingSet, setExisting]  = useState(null);
  const [loadErr,     setLoadErr]   = useState(null);
  const [creating,    setCreating]  = useState(false);
  const [log,         setLog]       = useState([]);
  const [filter,      setFilter]    = useState("all"); // all | missing | present

  const appendLog = (msg, type = "info") =>
    setLog(prev => [...prev, { msg, type, id: Date.now() + Math.random() }]);

  useEffect(() => {
    if (!token) {
      // Demo mode — simulate: existing 20, add-group missing
      const sim = new Set(ALL_COLS.filter(c => c.group !== "add").map(c => c.name));
      setExisting(sim);
    } else {
      loadColumns();
    }
  }, [token]);

  async function loadColumns() {
    setLoadErr(null);
    setExisting(null);
    try {
      const r = await fetch(
        `https://graph.microsoft.com/v1.0/sites/${CFG.SITE_ID}/lists/${CFG.LIST_ID}/columns?$select=name&$top=200`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error((await r.json()).error?.message || `HTTP ${r.status}`);
      const d = await r.json();
      setExisting(new Set(d.value.map(c => c.name)));
    } catch (e) {
      setLoadErr(e.message);
    }
  }

  async function createMissing() {
    const missing = ALL_COLS.filter(c => c.payload && !existingSet.has(c.name));
    if (!missing.length) return;
    setCreating(true);
    setLog([]);

    for (const col of missing) {
      appendLog(`Creating ${col.name}…`);
      try {
        const r = await fetch(
          `https://graph.microsoft.com/v1.0/sites/${CFG.SITE_ID}/lists/${CFG.LIST_ID}/columns`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(col.payload),
          }
        );
        if (r.ok) {
          appendLog(`✓ ${col.name}`, "success");
          setExisting(prev => new Set([...prev, col.name]));
        } else {
          const e = await r.json();
          appendLog(`✗ ${col.name}: ${e.error?.message || `HTTP ${r.status}`}`, "error");
        }
      } catch (e) {
        appendLog(`✗ ${col.name}: ${e.message}`, "error");
      }
      await new Promise(r => setTimeout(r, 400));
    }

    appendLog("─ Done", "dim");
    setCreating(false);
  }

  const present = existingSet ? ALL_COLS.filter(c => existingSet.has(c.name) || c.group === "built-in") : [];
  const missing = existingSet ? ALL_COLS.filter(c => c.group !== "built-in" && !existingSet.has(c.name)) : [];
  const complete = missing.length === 0 && existingSet !== null;

  const filtered = existingSet ? ALL_COLS.filter(c => {
    const isPresent = c.group === "built-in" || existingSet.has(c.name);
    if (filter === "missing")  return !isPresent && c.group !== "built-in";
    if (filter === "present")  return isPresent;
    return true;
  }) : ALL_COLS;

  const logColor = (t) => t === "success" ? "#22C55E" : t === "error" ? "#EF4444" : t === "dim" ? "#475569" : "#94A3B8";

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:20, fontWeight:600, color:C.text, margin:0 }}>SharePoint Column Setup</h1>
        <p style={{ fontSize:13, color:C.muted, margin:"3px 0 0" }}>
          Verify and auto-create all 32 required SDR-Submissions columns · {CFG.DEMO ? "Demo mode — simulated data" : "Live — connected to SharePoint"}
        </p>
      </div>

      {/* Status row */}
      {existingSet && (
        <div style={{ display:"flex", gap:12, marginBottom:20 }}>
          <KPICard label="Present" value={present.length} color="#22C55E" sub="columns verified" />
          <KPICard label="Missing" value={missing.length} color={missing.length > 0 ? "#EF4444" : "#22C55E"} sub={missing.length > 0 ? "need to be created" : "none — list complete"} />
          <KPICard label="Required" value={ALL_COLS.length} sub="total (incl. Title)" />

          <div style={{ flex:1, background:C.card, border:`1px solid ${complete ? "#BBF7D0" : C.border}`, borderRadius:12, padding:"16px 20px", display:"flex", flexDirection:"column", justifyContent:"center", gap:8 }}>
            {complete && (
              <div style={{ fontSize:13, color:"#166534", fontWeight:500 }}>✓ SDR-Submissions is complete</div>
            )}
            {!complete && !token && (
              <div style={{ fontSize:12, color:"#92400E", lineHeight:1.5 }}>
                <strong>Demo mode</strong> — real column creation requires signing in.<br/>
                Run <code style={{ background:"#FEF3C7", padding:"1px 5px", borderRadius:4 }}>node setup-sharepoint.js</code> in a terminal.
              </div>
            )}
            {!complete && token && (
              <button onClick={createMissing} disabled={creating}
                style={{ padding:"9px 18px", background:creating?"#93C5FD":C.blue, color:"#fff", border:"none", borderRadius:8, cursor:creating?"not-allowed":"pointer", fontSize:13, fontWeight:500 }}>
                {creating ? "Creating columns…" : `Create ${missing.length} missing column${missing.length !== 1 ? "s" : ""} →`}
              </button>
            )}
            {!complete && token && (
              <button onClick={loadColumns} style={{ background:"none", border:"none", fontSize:11, color:C.muted, cursor:"pointer", padding:0, textAlign:"left" }}>
                ↻ Refresh status
              </button>
            )}
          </div>
        </div>
      )}

      {/* Load error */}
      {loadErr && (
        <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#991B1B", marginBottom:16 }}>
          ✗ Could not read list columns: {loadErr}
          <br/><span style={{ fontSize:11 }}>Check that admin consent is granted for Sites.ReadWrite.All in your App Registration.</span>
        </div>
      )}

      {/* Filter tabs */}
      {existingSet && (
        <div style={{ display:"flex", gap:6, marginBottom:12 }}>
          {[["all","All columns"],["missing","Missing only"],["present","Present only"]].map(([val,lab]) => (
            <button key={val} onClick={() => setFilter(val)}
              style={{ padding:"5px 14px", borderRadius:99, fontSize:12, cursor:"pointer",
                background: filter===val ? C.blue : "#fff",
                color: filter===val ? "#fff" : C.muted,
                border: `1px solid ${filter===val ? C.blue : C.border}` }}>
              {lab}
            </button>
          ))}
        </div>
      )}

      {/* Column table */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden", marginBottom:16 }}>
        {!existingSet && !loadErr && (
          <div style={{ padding:32, textAlign:"center", color:C.muted, fontSize:13 }}>Loading column status…</div>
        )}
        {existingSet && filtered.length === 0 && (
          <div style={{ padding:32, textAlign:"center", color:C.muted, fontSize:13 }}>No columns match this filter</div>
        )}
        {existingSet && filtered.length > 0 && (
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ background:"#F8FAFC" }}>
                {["","Column name","Type","Description","Status"].map(h => (
                  <th key={h} style={{ padding:"9px 14px", textAlign:"left", fontSize:11, fontWeight:500, color:C.muted, borderBottom:`1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(col => {
                const isPresent = col.group === "built-in" || existingSet.has(col.name);
                const isNew     = col.group === "add";
                return (
                  <tr key={col.name} style={{ borderBottom:`1px solid ${C.border}`, background: !isPresent ? "#FFF9F9" : undefined }}>
                    <td style={{ padding:"10px 10px 10px 14px", fontSize:14, color: isPresent ? "#22C55E" : "#EF4444", fontWeight:600 }}>
                      {isPresent ? "✓" : "✗"}
                    </td>
                    <td style={{ padding:"10px 8px", fontFamily:"monospace", fontSize:12, fontWeight:500, color: isPresent ? C.text : "#991B1B" }}>
                      {col.name}
                      {isNew && <span style={{ marginLeft:6, fontSize:10, background:"#DBEAFE", color:"#1E40AF", padding:"1px 6px", borderRadius:99, fontFamily:"inherit" }}>new</span>}
                    </td>
                    <td style={{ padding:"10px 8px" }}><TypeTag type={col.type} /></td>
                    <td style={{ padding:"10px 8px", color:C.muted, fontSize:12, lineHeight:1.4 }}>{col.desc}</td>
                    <td style={{ padding:"10px 14px" }}>
                      {col.group === "built-in" && <span style={{ fontSize:11, color:C.muted }}>Built-in</span>}
                      {col.group !== "built-in" && isPresent  && <span style={{ fontSize:11, color:"#166534" }}>Present</span>}
                      {col.group !== "built-in" && !isPresent && <span style={{ fontSize:11, color:"#991B1B", fontWeight:500 }}>Missing</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Creation log */}
      {log.length > 0 && (
        <div style={{ background:"#0D1B2A", borderRadius:12, padding:"14px 18px", fontFamily:"monospace", fontSize:12, maxHeight:220, overflowY:"auto" }}>
          <div style={{ fontSize:10, color:"#475569", marginBottom:8, fontFamily:"inherit" }}>COLUMN CREATION LOG</div>
          {log.map(entry => (
            <div key={entry.id} style={{ color:logColor(entry.type), lineHeight:1.9 }}>{entry.msg}</div>
          ))}
        </div>
      )}

      {/* Terminal alternative */}
      <div style={{ marginTop:16, padding:"12px 16px", background:"#F8FAFC", border:`1px solid ${C.border}`, borderRadius:10, fontSize:12, color:C.muted, lineHeight:1.6 }}>
        <strong style={{ color:C.text }}>Prefer the terminal?</strong> The script below does the same thing — no browser needed:<br />
        <code style={{ fontFamily:"monospace", color:"#5B21B6" }}>node setup-sharepoint.js</code>
        {" "}(fill in the CONFIG values first, then run from your project folder)
      </div>
    </div>
  );
}

// ─── Navigation config ─────────────────────────────────────────────────────────
const NAV = [
  { id:"dashboard",      label:"Dashboard",       icon:"ti-layout-dashboard" },
  { id:"new-sdr",        label:"New SDR",          icon:"ti-plus"             },
  { id:"my-submissions", label:"My Submissions",   icon:"ti-file-text"        },
  { id:"approval-queue", label:"Approval Queue",   icon:"ti-checklist"        },
  { id:"search",         label:"Search & Archive", icon:"ti-search"           },
  { id:"form-library",   label:"Form Library",     icon:"ti-books"            },
  { id:"setup",          label:"Column Setup",     icon:"ti-database-cog"     },
];

// ══════════════════════════════════════════════════════════════════════════════
// APP ROOT
// Demo mode (no env vars): uses mock data and the hardcoded Raju Kakani user.
// Production mode (env vars set): calls getToken() on mount, loads real data.
// ══════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [authState, setAuthState] = useState(CFG.DEMO ? "ready" : "init");
  const [authError, setAuthError] = useState(null);
  const [token,  setToken]   = useState(null);
  const [user,   setUser]    = useState(CFG.DEMO ? { name:"Raju Kakani", mail:"rk2@sigmaco.com" } : null);
  const [sdrs,   setSDRs]    = useState(CFG.DEMO ? MOCK_SDRS : []);
  const [loading,setLoading] = useState(false);
  const [toast,  setToast]   = useState(null);
  const [screen, setScreen]  = useState("dashboard");

  // Auth initialisation — skipped in demo mode
  useEffect(() => {
    if (CFG.DEMO) return;
    (async () => {
      try {
        const t = await getToken();
        setToken(t);
        const me = await Api.getMe(t);
        setUser({ name: me.displayName, mail: me.mail });
        setAuthState("ready");
      } catch (e) {
        setAuthError(e.message || "Sign-in failed or was cancelled");
        setAuthState("error");
      }
    })();
  }, []);

  // When a real token is available, swap mock data for live SharePoint data
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Api.listSDRs(token)
      .then(setSDRs)
      .catch(e => showToast(e.message, "error"))
      .finally(() => setLoading(false));
  }, [token]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  };

  // Only send fields that exist as SharePoint columns — strips email helpers and React state
  // Note: SDRNumber internal name is SDRNumber0 (SharePoint auto-appended 0 on creation)
  const SP_FIELDS = [
    'SupplierName','SupplierLocation','SupplierContactName','SupplierContactInfo',
    'PartName','PartNumber','PartDrawingRev','AffectedProjects','AffectedBatch','CutOffSerialNumber',
    'DeviationDescription','ReasonForDeviation','Consequences','RectificationPlan',
    'EngineeringComments','SupplyChainComments','RejectionComments',
    'ChangeType','DeviationType','DeviationPermittedTime','DeviationPermittedQty','CompletedForm',
  ];
  const cleanForSP = (form) => {
    const out = { Title: form.SDRNumber || 'SDR' };
    // SDRNumber internal name is SDRNumber0 in SharePoint
    if (form.SDRNumber) out.SDRNumber0 = form.SDRNumber;
    SP_FIELDS.forEach(f => { if (form[f] !== undefined && form[f] !== '') out[f] = form[f]; });
    return out;
  };

  const handleSubmit = async (formData) => {
    const fields = cleanForSP(formData);
    if (token) {
      try {
        await Api.createSDR(token, fields);
        const em = Email.engRequest(formData);
        await Api.sendMail(token, em.to, em.subject, em.html);
        setSDRs(await Api.listSDRs(token));
      } catch(e) { showToast(`Submit failed: ${e.message}`, "error"); return; }
    } else {
      setSDRs(prev => [{ ...formData, id:Date.now(), _itemId:String(Date.now()), Status:"Pending Engineering" }, ...prev]);
    }
    showToast(`${formData.SDRNumber} submitted — ${formData.EngineeringApprover} notified by email`);
  };

  const handleApprove = async (sdr, comments) => {
    const isEng = sdr.Status === "Pending Engineering";
    const now   = new Date().toISOString();
    const updates = isEng
      ? { Status:"Pending Supply Chain", EngineeringComments:comments, EngineeringApprovedDate:now }
      : { Status:"Closed", SupplyChainComments:comments, SupplyChainApprovedDate:now, ClosedDate:now };
    if (token) {
      await Api.patchSDR(token, sdr._itemId, updates);
      const em = isEng ? Email.scRequest({...sdr,...updates}) : Email.closed(sdr);
      await Api.sendMail(token, em.to, em.subject, em.html);
      setSDRs(await Api.listSDRs(token));
    } else {
      setSDRs(prev => prev.map(s => (s.id===sdr.id||s._itemId===sdr._itemId) ? {...s,...updates} : s));
    }
    showToast(isEng
      ? `Approved → Pending Supply Chain · ${sdr.SupplyChainApprover} notified`
      : `${sdr.SDRNumber} closed · ${sdr.SubmittedBy} notified`);
  };

  const handleReject = async (sdr, comments) => {
    const updates = { Status:"Rejected", RejectionComments:comments };
    if (token) {
      await Api.patchSDR(token, sdr._itemId, updates);
      const em = Email.rejected(sdr, user.name, comments);
      await Api.sendMail(token, em.to, em.subject, em.html);
      setSDRs(await Api.listSDRs(token));
    } else {
      setSDRs(prev => prev.map(s => (s.id===sdr.id||s._itemId===sdr._itemId) ? {...s,...updates} : s));
    }
    showToast(`${sdr.SDRNumber} rejected · ${sdr.SubmittedBy} notified`, "warning");
  };

  const pending  = sdrs.filter(s => s.Status==="Pending Engineering"||s.Status==="Pending Supply Chain");
  const initials = (user?.name||"RK").split(" ").map(w=>w[0]).join("").slice(0,2);

  // Auth screens
  if (authState === "init") return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:500, gap:12, fontFamily:"inherit" }}>
      <div style={{ fontSize:14, fontWeight:700, color:"#0F172A", letterSpacing:1 }}>SIGMA</div>
      <div style={{ fontSize:13, color:"#64748B" }}>Signing you in to Microsoft 365…</div>
    </div>
  );

  if (authState === "error") return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:500, gap:12, fontFamily:"inherit" }}>
      <div style={{ fontSize:14, fontWeight:700, color:"#0F172A", letterSpacing:1 }}>SIGMA</div>
      <div style={{ fontSize:13, color:"#991B1B", maxWidth:340, textAlign:"center", lineHeight:1.5 }}>{authError}</div>
      <button onClick={() => window.location.reload()} style={{ padding:"8px 18px", border:"1px solid #E2E8F0", borderRadius:8, cursor:"pointer", fontSize:13 }}>Try again</button>
    </div>
  );

  const toastStyle = (type) => ({
    background: type==="error" ? "#FEF2F2" : type==="warning" ? "#FFFBEB" : "#F0FDF4",
    borderBottom: `1px solid ${type==="error" ? "#FECACA" : type==="warning" ? "#FDE68A" : "#BBF7D0"}`,
    color: type==="error" ? "#991B1B" : type==="warning" ? "#92400E" : "#166534",
  });

  const screens = {
    dashboard:       <Dashboard sdrs={sdrs} loading={loading} />,
    "new-sdr":       <NewSDR user={user} onSubmit={handleSubmit} />,
    "my-submissions":<MySubmissions sdrs={sdrs} />,
    "approval-queue":<ApprovalQueue sdrs={sdrs} user={user} onApprove={handleApprove} onReject={handleReject} />,
    search:          <SearchArchive sdrs={sdrs} />,
    "form-library":  <FormLibrary />,
    "setup":         <Setup token={token} />,
  };

  return (
    <div style={{ fontFamily:"inherit" }}>

      {/* Demo banner */}
      {CFG.DEMO && (
        <div style={{ background:"#FFFBEB", borderBottom:"1px solid #FDE68A", padding:"7px 20px", fontSize:11, color:"#92400E", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
          <strong>Demo mode</strong><span>·</span>
          <span>Mock data · Set CFG.DEMO = false + fill in Azure AD credentials to connect live SharePoint</span>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ ...toastStyle(toast.type), padding:"9px 24px", fontSize:13, display:"flex", alignItems:"center", gap:8 }}>
          <span>{toast.type==="error" ? "✗" : toast.type==="warning" ? "⚠" : "✓"}</span>
          {toast.msg}
        </div>
      )}

      <div style={{ display:"flex", minHeight:820, background:C.bg }}>

        {/* Sidebar */}
        <div style={{ width:220, background:C.navy, display:"flex", flexDirection:"column", flexShrink:0 }}>
          <div style={{ background:"#ffffff", padding:"12px 18px", display:"flex", alignItems:"center", justifyContent:"flex-start" }}>
            <img src="/sigma-logo.png" alt="Sigma Engineering" style={{ width:130, height:"auto", display:"block" }} />
          </div>
          <div style={{ padding:"5px 16px 6px", background:C.navy, borderBottom:"1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.45)", letterSpacing:1 }}>ENGINEERING · QC FORMS</div>
          </div>
          <nav style={{ flex:1, padding:"10px 8px" }}>
            {NAV.map(item => {
              const active = screen === item.id;
              return (
                <button key={item.id} onClick={() => setScreen(item.id)}
                  style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"9px 12px", marginBottom:2,
                    background: active ? "rgba(13,148,136,0.18)" : "transparent",
                    border:"none", borderLeft:`3px solid ${active ? C.blue : "transparent"}`,
                    borderRadius:"0 8px 8px 0", cursor:"pointer", textAlign:"left",
                    color: active ? "#99E6E0" : "rgba(255,255,255,0.5)", fontSize:13 }}>
                  <i className={`ti ${item.icon}`} style={{ fontSize:15, flexShrink:0 }} aria-hidden="true" />
                  <span style={{ flex:1 }}>{item.label}</span>
                  {item.id==="approval-queue" && pending.length > 0 && (
                    <span style={{ background:C.blue, color:"#fff", borderRadius:99, padding:"1px 7px", fontWeight:600, fontSize:10 }}>
                      {pending.length}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
          <div style={{ padding:"14px 16px", borderTop:"1px solid rgba(255,255,255,0.07)", display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:"50%", background:"#1D4ED8", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:"#fff", flexShrink:0 }}>
              {initials}
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:500, color:"#fff" }}>{user?.name}</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)" }}>{CFG.DEMO ? "Demo · Engineering" : user?.mail}</div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div style={{ flex:1, overflowY:"auto", padding:"28px 32px" }}>
          {screens[screen]}
        </div>
      </div>
    </div>
  );
}
